import { IconButton, InputAdornment, TextField } from "@mui/material";
import React, { useState } from "react";
import styles from "./TextInput.module.scss";

export interface TextInputProps {
  label?: string;
  value?: unknown;
  onChange?:
    | React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
    | undefined;
  multiline?: boolean | undefined;
  rows?: number | undefined;
  placeholder?: string | undefined;
  error?: boolean;
  style?: React.CSSProperties | undefined;
  type?: React.HTMLInputTypeAttribute;
  variant?: "dark" | "light";
  withShowPasswordBtn?: boolean;
  disabled?: boolean;
  iconLeft?: React.ReactNode;
  InputProps?: React.ComponentProps<typeof TextField>["InputProps"];
}
export default function TextInput({
  label,
  value,
  onChange,
  multiline,
  rows,
  placeholder,
  error,
  style,
  type,
  variant,
  withShowPasswordBtn,
  disabled,
  iconLeft,
  InputProps: customInputProps
}: TextInputProps) {
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };
  if (variant === "dark") {
    return (
      <TextInputMJB
        style={style}
        error={error}
        label={label}
        multiline={multiline}
        rows={rows}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        type={type}
      />
    );
  }

  return (
    <>
      <TextField
        disabled={disabled}
        style={style}
        error={error}
        id="outlined-multiline"
        label={label}
        multiline={multiline}
        rows={rows}
        fullWidth
        value={value}
        InputLabelProps={{ shrink: ((!!value) || (value !== ""))}}
        onChange={onChange}
        placeholder={placeholder}
        type={showPassword ? "text" : type}
        InputProps={{
          ...customInputProps,
          startAdornment:iconLeft ? (
            <InputAdornment position="start">
             {iconLeft}
            </InputAdornment>
          ): customInputProps?.startAdornment,
          endAdornment:
            type === "password" ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  style={{ marginRight: 0 }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ) : customInputProps?.endAdornment || null,
        }}
      />
    </>
  );
}

const TextInputMJB = ({
  label,
  value,
  onChange,
  type,
  error,
}: TextInputProps) => {
  const errorLabel = error ? "error" : "";
  return (
    <div
      className={[styles["input"], styles["input--" + errorLabel]].join(" ")}
    >
      <span
        className={[
          styles["input__label"],
          styles["input__label--" + errorLabel],
        ].join(" ")}
      >
        {label}
      </span>
      <div
        className={[
          styles["input__input-container"],
          styles["input__input-container--" + errorLabel],
        ].join(" ")}
      >
        <input
          type={type}
          className={[
            styles["input__input-container__input-item"],
            styles["input__input-container__input-item--" + errorLabel],
          ].join(" ")}
          value={value as string | number}
          onChange={onChange}
        />
      </div>
    </div>
  );
};

export const VisibilityOff = ({
  size = 25,
  color = "gray",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    width={size}
    viewBox="0 0 22 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
   
  >
    <path
      d="M11 4C13.76 4 16 6.24 16 9C16 9.65 15.87 10.26 15.64 10.83L18.56 13.75C20.07 12.49 21.26 10.86 21.99 9C20.26 4.61 15.99 1.5 10.99 1.5C9.59 1.5 8.25 1.75 7.01 2.2L9.17 4.36C9.74 4.13 10.35 4 11 4ZM1 1.27L3.28 3.55L3.74 4.01C2.06842 5.30786 0.776644 7.03138 0 9C1.73 13.39 6 16.5 11 16.5C12.55 16.5 14.03 16.2 15.38 15.66L15.8 16.08L18.73 19L20 17.73L2.27 0L1 1.27ZM6.53 6.8L8.08 8.35C8.03 8.56 8 8.78 8 9C8 10.66 9.34 12 11 12C11.22 12 11.44 11.97 11.65 11.92L13.2 13.47C12.53 13.8 11.79 14 11 14C8.24 14 6 11.76 6 9C6 8.21 6.2 7.47 6.53 6.8ZM10.84 6.02L13.99 9.17L14.01 9.01C14.01 7.35 12.67 6.01 11.01 6.01L10.84 6.02Z"
      fill={color}
    />
  </svg>
);

export const Visibility = ({
  size = 25,
  color = "gray",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    width={size}
    viewBox="0 0 22 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  
  >
    <path
      d="M11 12C12.25 12 13.3127 11.5627 14.188 10.688C15.0633 9.81333 15.5007 8.75067 15.5 7.5C15.4993 6.24933 15.062 5.187 14.188 4.313C13.314 3.439 12.2513 3.00133 11 3C9.74867 2.99867 8.68633 3.43633 7.813 4.313C6.93967 5.18967 6.502 6.252 6.5 7.5C6.498 8.748 6.93567 9.81067 7.813 10.688C8.69033 11.5653 9.75267 12.0027 11 12ZM11 10.2C10.25 10.2 9.61267 9.93733 9.088 9.412C8.56333 8.88667 8.30067 8.24933 8.3 7.5C8.29933 6.75067 8.562 6.11333 9.088 5.588C9.614 5.06267 10.2513 4.8 11 4.8C11.7487 4.8 12.3863 5.06267 12.913 5.588C13.4397 6.11333 13.702 6.75067 13.7 7.5C13.698 8.24933 13.4357 8.887 12.913 9.413C12.3903 9.939 11.7527 10.2013 11 10.2ZM11 15C8.56667 15 6.35 14.321 4.35 12.963C2.35 11.605 0.9 9.784 0 7.5C0.9 5.21667 2.35 3.396 4.35 2.038C6.35 0.680001 8.56667 0.000667157 11 4.90196e-07C13.4333 -0.000666176 15.65 0.678667 17.65 2.038C19.65 3.39733 21.1 5.218 22 7.5C21.1 9.78333 19.65 11.6043 17.65 12.963C15.65 14.3217 13.4333 15.0007 11 15Z"
      fill={color}
    />
  
  </svg>
);
